import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Palette,
  Circle,
  Droplet,
  Sparkles,
  Leaf,
  Sun,
  Heart,
  Waves
} from 'lucide-react';

/**
 * Floating color scheme selector button
 * Saves selected color scheme to localStorage
 */
export const ColorSchemeSelector = ({ onColorChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(() => {
    return localStorage.getItem('backgroundColorScheme') || 'orange';
  });

  const colorSchemes = [
    { name: 'gray', label: 'Gray', color: '#9e9e9e', Icon: Circle },
    { name: 'blue', label: 'Blue', color: '#2196f3', Icon: Droplet },
    { name: 'purple', label: 'Purple', color: '#9c27b0', Icon: Sparkles },
    { name: 'green', label: 'Green', color: '#4caf50', Icon: Leaf },
    { name: 'orange', label: 'Orange', color: '#ff9800', Icon: Sun },
    { name: 'red', label: 'Red', color: '#f44336', Icon: Heart },
    { name: 'teal', label: 'Teal', color: '#009688', Icon: Waves },
  ];

  const handleColorSelect = (colorName) => {
    setSelectedColor(colorName);
    localStorage.setItem('backgroundColorScheme', colorName);
    onColorChange(colorName);
    setIsOpen(false);
  };

  const getCurrentColor = () => {
    return colorSchemes.find(c => c.name === selectedColor) || colorSchemes[4]; // default orange
  };

  return (
    <div style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 100 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              bottom: '70px',
              right: 0,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
              minWidth: '200px',
            }}
            className="color-scheme-panel"
          >
            <div style={{ marginBottom: '8px', padding: '8px 4px', fontSize: '13px', fontWeight: 600, color: '#333' }}>
              Background Color
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {colorSchemes.map((scheme) => {
                const IconComponent = scheme.Icon;
                return (
                  <motion.button
                    key={scheme.name}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleColorSelect(scheme.name)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      border: selectedColor === scheme.name ? `2px solid ${scheme.color}` : '2px solid transparent',
                      borderRadius: '10px',
                      background: selectedColor === scheme.name ? `${scheme.color}15` : 'rgba(0, 0, 0, 0.03)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: '13px',
                      fontWeight: selectedColor === scheme.name ? 600 : 500,
                      color: selectedColor === scheme.name ? scheme.color : '#555',
                    }}
                  >
                    <IconComponent size={18} strokeWidth={2} />
                    <span>{scheme.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          border: 'none',
          background: `linear-gradient(135deg, ${getCurrentColor().color}dd, ${getCurrentColor().color})`,
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.3s ease',
        }}
        className="color-scheme-fab"
      >
        <Palette size={24} strokeWidth={2} />
      </motion.button>

      <style>{`
        .color-scheme-panel {
          animation: slideUp 0.2s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .color-scheme-fab:hover {
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .dark .color-scheme-panel {
          background: rgba(30, 30, 35, 0.95);
        }

        .dark .color-scheme-panel > div:first-child {
          color: #f5f5f7;
        }

        .dark .color-scheme-panel button {
          background: rgba(255, 255, 255, 0.05);
          color: #a1a1a6;
        }

        .dark .color-scheme-panel button[style*="border: 2px solid"] {
          background: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};
